import { Button, DataTable, Form, FormSection, Placeholder } from '@hai/ui-react';
import { HaiDataTableColumnType } from '@hai/ui-react/dist/types';
import { observer } from 'mobx-react';
import { head } from 'ramda';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APIPath } from 'src/js/api/route-path-index';
import { compileWithArgs } from 'src/js/helper/path-to-regexp-helper';
import { useListSelectionWithDataStore } from 'src/js/provider/list-selection-with-data-store-provider';
import { openNewWindow } from 'src/js/util/global-util';

import { LicenseViewModel } from './license-view-model';

interface Props {
  onDelete?: VoidFunction;
  showImport: VoidFunction;
  loading?: boolean;
}

export const LicenseTable: React.FunctionComponent<Props> = observer(({ onDelete, showImport, loading = false }) => {
  const { t } = useTranslation();
  const dataStore = useListSelectionWithDataStore<LicenseViewModel>();

  const bulkActionComponents = () => {
    return (
      <Button state="idle" size="small" onClick={onDelete}>
        {t('general.deleteBtn')}
      </Button>
    );
  };

  const handleAction = (eventKey: string, data: any) => {
    switch (eventKey) {
      case 'view':
        openNewWindow(compileWithArgs(APIPath.licenses.download)({ name: data.name }), data.name);
        break;

      case 'delete':
        dataStore.unselectAll();
        dataStore.selectRow(data.index);
        onDelete();
        break;

      default:
        break;
    }
  };

  const columnStructure = [
    { fieldKey: 'name', title: t('general.name') },
    { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
  ];

  const [selectAll, setSelectAll] = useState(false);
  const handleCheckAll = (checked: boolean) => {
    setSelectAll(checked);
    checked ? dataStore.selectAll() : dataStore.unselectAll();
  };

  const PlaceHolderPage = () => {
    return <Placeholder.Table columnStructure={columnStructure} numRows={3} showTitlebar />;
  };

  return (
    <Form>
      <FormSection className="hai-p-0">
        {loading ? (
          <PlaceHolderPage />
        ) : (
          <DataTable
            selectable={true}
            sortable={false}
            columnStructure={columnStructure}
            onCheckAll={handleCheckAll}
            checkAll={selectAll}
            className="datatable-form-section-title"
            emptyStateText={t('settings.licensing.addFirst')}
          >
            <DataTable.TitleBar
              title={t('settings.licensing.sectionTitle')}
              rightComponent={
                <Button size="small" onClick={showImport}>
                  {t('general.install')}
                </Button>
              }
            />
            <DataTable.Header bulkActions={bulkActionComponents} />
            {dataStore.data.map((value: LicenseViewModel, index: number) => (
              <DataTable.Row
                key={`${value.name}-${index}`}
                rowData={{
                  index: index,
                  id: `${value.name}-${index}`,
                  name: head(value.name.split('.lic')),
                }}
                onSelect={() => dataStore.toggleRow(index)}
                checked={dataStore.isSelected(index)}
                actionItems={[
                  {
                    actionIcon: 'GoTo',
                    title: t('general.view'),
                    eventKey: 'view',
                    onSelect: handleAction,
                  },
                  {
                    actionIcon: 'TrashCan',
                    title: t('general.delete'),
                    eventKey: 'delete',
                    onSelect: handleAction,
                  },
                ]}
              ></DataTable.Row>
            ))}
          </DataTable>
        )}
      </FormSection>
    </Form>
  );
});
